// render - dashboard

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import { canManageNcvetQuestionBankContent, isOrganizationNcvetEffective } from 'utils/ncvetQuestionBankAccess';
import Loadable from 'components/Loadable';
import MainLayout from 'layout/MainLayout';
import CommonLayout from 'layout/CommonLayout';
import ContentLayout from 'layout/ContentLayout';
import { TabProvider } from 'contexts/TabContext';
import AuthGuard from 'utils/route-guard/AuthGuard';
import AIChatBot from 'pages/chat/AIChatBot';
import SuperAdminCourseList from 'pages/course/SuperAdminCourseList';
import SuperAdminCourseView from 'pages/course/myCourse/SuperAdminCourseView';
import ErrorLogs from 'pages/ErrorLogs/ErrorLogsList';
import EmailTemplateList from 'pages/notification/EmailTemplates';
import WeeklyReportList from 'pages/weeklyReport/WeeklyReportList';
import Time from 'react-timer-hook/dist/types/src/utils/Time';
import TimeTableViewer from 'pages/timetable-scheduler/TimeTableViewer';
import TimeOff from 'pages/Workload/TimeOff';
import AddTeacherWorkload from 'pages/Workload/AddTeacherWorkload';
import TeacherWorkloadList from 'pages/Workload/TeacherWorkloadList';
import AttendanceCheck from 'pages/Attendance/AttendanceCheck';
import AttendanceReport from 'pages/Attendance/AttendanceReport';
import TrainingCenterList from 'pages/TrainingCenter';
import AddTrainingCenter from 'pages/TrainingCenter/AddTrainingCenter';
import SkillSectorList from 'pages/SkillSector/SkillSectorList';
import AddSkillSector from 'pages/SkillSector/AddSkillSector';
import AddQualificationPack from 'pages/QualificationPack/AddQualificationPack';
import QualificationPackList from 'pages/QualificationPack/QualificationList';
import NOSList from 'pages/Nos/NosList';
import AddNOS from 'pages/Nos/AddNos';
import PerformanceCriteriaList from 'pages/PerformanceCriteria/PerformanceCriteriaList';
import AddPerformanceCriteria from 'pages/PerformanceCriteria/AddPerformanceCriteria';
const NcvetQuestionBankList = Loadable(lazy(() => import('pages/NcvetQuestionBank/NcvetQuestionBankList')));
const AddNcvetQuestion = Loadable(lazy(() => import('pages/NcvetQuestionBank/AddNcvetQuestion')));
const RubricList = Loadable(lazy(() => import('pages/NcvetRubric/RubricList')));
const AddRubric = Loadable(lazy(() => import('pages/NcvetRubric/AddRubric')));

const PrivateRoute = Loadable(lazy(() => import('utils/route-guard/privateRoute')));
const OrderHistory = Loadable(lazy(() => import('layout/MainLayout/Header/HeaderContent/Profile/orderHistory')));

// Customize landing page
const Home = Loadable(lazy(() => import('pages/CustomizeLandingPage/index')));
const PrivacyPolicy = Loadable(lazy(() => import('pages/CustomizeLandingPage/privacyPolicy')));
const TermsOfServices = Loadable(lazy(() => import('pages/CustomizeLandingPage/termsOfServices')));

// Feedback
const FeedbackForm = Loadable(lazy(() => import('pages/feedback/FeedbackForm')));
const FeedbackTable = Loadable(lazy(() => import('pages/feedback/FeedbackTable')));

// Contact
const ContactTable = Loadable(lazy(() => import('pages/contact/ContactList')));

// Packages
const Packages = Loadable(lazy(() => import('pages/packages/packages')));
const addPackage = Loadable(lazy(() => import('pages/packages/addPackages')));
const packageList = Loadable(lazy(() => import('pages/packages/packageList')));
const assignPackage = Loadable(lazy(() => import('pages/packages/AssignPackage')));
const Packagehistory = Loadable(lazy(() => import('pages/packages/packagePayment')));
const StudentPackages = Loadable(lazy(() => import('pages/packages/studentPackages')));

// App Package
const appPackage = Loadable(lazy(() => import('pages/AppPackage/appPackageList')));
const addAppPackage = Loadable(lazy(() => import('pages/AppPackage/appPackageForm')));

// Quiz
const QuizView = Loadable(lazy(() => import('pages/quiz/QuizView')));
const QuizStepper = Loadable(lazy(() => import('pages/quiz/QuizStepper')));
const AddQuizGroup = Loadable(lazy(() => import('pages/quiz/AddQuizGroup')));
const AddQuestions = Loadable(lazy(() => import('pages/quiz/AddQuestions')));
const QuestionTable = Loadable(lazy(() => import('pages/quiz/QuestionTable')));
const ResultHistory = Loadable(lazy(() => import('pages/quiz/ResultHistory')));
const QuizGroupList = Loadable(lazy(() => import('pages/quiz/quizGroupList')));
const QuizGroupTabs = Loadable(lazy(() => import('pages/quiz/QuizGroupTabs')));
const SubjectInfo = Loadable(lazy(() => import('pages/quiz/SubjectInfo')));
const BoardInfo = Loadable(lazy(() => import('pages/quiz/BoardInfo')));
const SubjectFormOption = Loadable(lazy(() => import('pages/quiz/SubjectFormOpition')));
const AssignOrganization = Loadable(lazy(() => import('pages/quiz/AssignToOrg')));

const AssessmentList = Loadable(lazy(() => import('pages/quiz/AssessmentList')));
const FreeCoursesList = Loadable(lazy(() => import('pages/quiz/FreeCoursesList')));
const OverallAssessment = Loadable(lazy(() => import('pages/quiz/OverallAssessment')));
const SkillMatrixStepper = Loadable(lazy(() => import('pages/quiz/SkillMatixStepper')));

// Badges
const Badges = Loadable(lazy(() => import('pages/badges/badges')));
const BadgesList = Loadable(lazy(() => import('pages/badges/BadgesList')));
const AddBadgeForm = Loadable(lazy(() => import('pages/badges/NewAddBadge')));
const BadgesAchieved = Loadable(lazy(() => import('pages/badges/BadgesAchieved')));

// User types
const UserTypeList = Loadable(lazy(() => import('pages/userType/userTypeList')));
const UserTypeForm = Loadable(lazy(() => import('pages/userType/userTypeForm')));

// Exams
const AddExam = Loadable(lazy(() => import('pages/Exam/AddExam')));
const DemoExam = Loadable(lazy(() => import('pages/Exam/DemoExam')));
const ExamTable = Loadable(lazy(() => import('pages/Exam/ExamTable')));
const ResultTable = Loadable(lazy(() => import('pages/Exam/ResultTable')));
const ExamStepper = Loadable(lazy(() => import('pages/Exam/ExamStepper')));
const AddExamSection = Loadable(lazy(() => import('pages/Exam/AddSections')));
const AnswerChecking = Loadable(lazy(() => import('pages/Exam/AnswerChecking')));
const OMRConfig = Loadable(lazy(() => import('pages/Exam/OMRConfigList')));
const AddEditOMRConfig = Loadable(lazy(() => import('pages/Exam/AddEditOMRConfig')));
const FinalReportCard = Loadable(lazy(() => import('pages/Exam/FinalReportCard')));
const ResultDetailsTable = Loadable(lazy(() => import('pages/Exam/ResultDetails')));
const ResultReportCard = Loadable(lazy(() => import('pages/Exam/ResultReportCard')));

// Support ticket
const AllTicket = Loadable(lazy(() => import('pages/Support-Ticket/allTicket')));
const AddTicket = Loadable(lazy(() => import('pages/Support-Ticket/addTicket')));
const TcketTable = Loadable(lazy(() => import('pages/Support-Ticket/ticketTable')));

// Subject

const SubjectList = Loadable(lazy(() => import('pages/Subject/SubjectList')));

// Superadmin
const OrgTabs = Loadable(lazy(() => import('pages/superAdmin/organizationTabs')));
const FormRenderer = Loadable(lazy(() => import('pages/superAdmin/LandingPageForm')));
const Organization = Loadable(lazy(() => import('pages/superAdmin/OrgnizationList')));
const SuperUsers = Loadable(lazy(() => import('pages/superAdmin/SuperAdminUserList')));
const LandingPageTabs = Loadable(lazy(() => import('pages/superAdmin/LandingPageTabs')));
const SuperUsersAdd = Loadable(lazy(() => import('pages/superAdmin/SuperAdminUserAdd')));
const AddOrganization = Loadable(lazy(() => import('pages/superAdmin/AddOrganization')));
const ContentManagement = Loadable(lazy(() => import('pages/superAdmin/ContentManagement')));
const ContentManagementList = Loadable(lazy(() => import('pages/superAdmin/ContentManagementList')));
const AssignedOrgnizationList = Loadable(lazy(() => import('pages/superAdmin/AssignedOrganizations')));
const AppVersionList = Loadable(lazy(() => import('pages/AppVersion/AppVersionForm')));

// Timeline
const TimeLineTable = Loadable(lazy(() => import('pages/timeline/TimeLine')));

// Certificates
const ViewCertificate = Loadable(lazy(() => import('pages/Certificate/viewCertificate')));
const CertificateTable = Loadable(lazy(() => import('pages/Certificate/CertificateTable')));
const VerifyCertificate = Loadable(lazy(() => import('pages/Certificate/VerifyCertificate')));
const MainCertificate = Loadable(lazy(() => import('pages/Certificate/CertificateOperations')));
const UserCertificateTable = Loadable(lazy(() => import('pages/Certificate/UserCertificateTable')));

//Email Templates
const EmailTemplates = Loadable(lazy(() => import('pages/EmailTemplate/EmailTemplates')));
const EmailTemplateForm = Loadable(lazy(() => import('pages/EmailTemplate/EmailTemplateForm')));
const CustomEmailTemplateForm = Loadable(lazy(() => import('pages/notification/CustomEmailTemplateForm')));

// Assignments
const Assignment = Loadable(lazy(() => import('pages/Assignment/Assignment')));
const ViewAssignment = Loadable(lazy(() => import('pages/Assignment/viewAssignment')));
const AssignmentList = Loadable(lazy(() => import('pages/Assignment/AssignmentList')));
const CheckAssignment = Loadable(lazy(() => import('pages/Assignment/CheckAssignment')));

// Dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard')));
const MyActivity = Loadable(lazy(() => import('pages/dashboard/MyActivity')));
const FacultyDashboard = Loadable(lazy(() => import('pages/dashboard/instructor')));
const LearnerDashboard = Loadable(lazy(() => import('pages/dashboard/StudentDashboard')));

// Getting started
const GetStarted = Loadable(lazy(() => import('pages/GetStarted/GetStarted')));

// AI Lesson
const AiLessonList = Loadable(lazy(() => import('pages/AiLesson/AiLessonsList')));
const AiLessonsForm = Loadable(lazy(() => import('pages/AiLesson/AiLessonForm')));

// Users
const UserList = Loadable(lazy(() => import('pages/users/index')));
const AddUser = Loadable(lazy(() => import('pages/users/AddUser')));
const UserDashboard = Loadable(lazy(() => import('pages/users/UserDashboard')));

// Whiteboard
const WhiteboardList = Loadable(lazy(() => import('pages/course/Whiteboard')));
const RecordingsList = Loadable(lazy(() => import('pages/course/Whiteboard/RecordingsList')));
const RecordingPlayer = Loadable(lazy(() => import('pages/course/Whiteboard/RecordingPlayer')));
const CourseWhiteboard = Loadable(lazy(() => import('pages/course/Whiteboard/CourseWhiteboard')));
const EditWhiteboardRecording = Loadable(lazy(() => import('pages/course/Whiteboard/EditRecording')));

// Courses
const CourseForm = Loadable(lazy(() => import('pages/course/courseForm')));
const MyCourse = Loadable(lazy(() => import('pages/course/myCourse/MyCourse')));
const Wishlist = Loadable(lazy(() => import('pages/course/wishlist/wishlist')));
const CourseDetails = Loadable(lazy(() => import('pages/course/courseDetails')));
const CourseOverView = Loadable(lazy(() => import('pages/course/Content/Overview')));
const CourseListTable = Loadable(lazy(() => import('pages/course/courseListTable')));
const Checkout = Loadable(lazy(() => import('pages/course/courseCheckout/checkout')));
const CourseData = Loadable(lazy(() => import('pages/course/courseData/courseData')));
const VideoPlayer = Loadable(lazy(() => import('pages/course/myCourse/VideoPlayer')));
const UserReports = Loadable(lazy(() => import('pages/course/UserReport/UserReports')));
const AssessorReport = Loadable(lazy(() => import('pages/course/AssessorReport/AssessorReport')));
const AssessorDashboard = Loadable(lazy(() => import('pages/course/AssessorReport/AssessorDashboard')));
const CourseReports = Loadable(lazy(() => import('pages/course/Content/CourseReports')));
const CourseFormSA = Loadable(lazy(() => import('pages/course/SuperAdmin/courseFromSA')));
const CourseTableSA = Loadable(lazy(() => import('pages/course/SuperAdmin/courseTableSA')));
const EditContentDetail = Loadable(lazy(() => import('pages/course/contents/editContent')));
const AddAssignment = Loadable(lazy(() => import('pages/course/courseData/AddAssignment')));
const CourseDashboard = Loadable(lazy(() => import('pages/course/Content/CourseDashboard')));
const AssignmentView = Loadable(lazy(() => import('pages/course/courseData/ViewAssignment')));
const CourseCatalog = Loadable(lazy(() => import('pages/course/courseCatalog/CourseCatalog')));
const CourseCatalogWithLayout = Loadable(lazy(() => import('pages/course/courseCatalog/CourseCatalogWithLayout')));
const AssessmentReport = Loadable(lazy(() => import('pages/course/Assessment/AssessmentReport')));
const CourseReAssign = Loadable(lazy(() => import('pages/course/CourseReAssignment/CourseReAssign')));
const CourseCatalogList = Loadable(lazy(() => import('pages/course/courseCatalog/courseCatalogList')));
const AssessmentDashboard = Loadable(lazy(() => import('pages/course/Assessment/AssessmentDashboard')));
const AssignmentUserTable = Loadable(lazy(() => import('pages/course/Content/CourseAssignmentUserTable')));
const AddCourseReAssign = Loadable(lazy(() => import('pages/course/CourseReAssignment/AddEditCourseToReAssign')));
const CoursePayment = Loadable(lazy(() => import('pages/course/CoursePayment/CoursePayment')));

// Discussion
const DiscussionHome = Loadable(lazy(() => import('pages/discussion/DiscussionHome')));
const DiscussionPage = Loadable(lazy(() => import('pages/discussion/DiscussionPage')));

// Groups
const GroupList = Loadable(lazy(() => import('pages/group/index')));
const AddGroup = Loadable(lazy(() => import('pages/group/AddGroup')));
const GroupData = Loadable(lazy(() => import('pages/group/GroupData')));

// Dictionary
const Dictionary = Loadable(lazy(() => import('pages/Dictionary')));

// Notification
const AllNotify = Loadable(lazy(() => import('pages/notify/index')));
const Notification = Loadable(lazy(() => import('pages/notification/index')));
const AddNotification = Loadable(lazy(() => import('pages/notification/AddNotification')));
const CustomNotification = Loadable(lazy(() => import('pages/notification/CustomNotification')));
const AddCustomNotification = Loadable(lazy(() => import('pages/notification/SendCustomNotification')));

//Batch
const BatchList = Loadable(lazy(() => import('pages/Batch/index')));
const AddBatch = Loadable(lazy(() => import('pages/Batch/AddBatch')));
const BatchCourseDetails = Loadable(lazy(() => import('pages/Batch/BatchCourseDetails')));
const BatchStudentsResults = Loadable(lazy(() => import('pages/Batch/BatchStudentsResults')));
const AddAssessor = Loadable(lazy(() => import('pages/Assessor/AddAssessor')));
// Blogs
const Blogs = Loadable(lazy(() => import('pages/Blogs/blogs')));
const BlogForm = Loadable(lazy(() => import('pages/Blogs/blogForm')));
const BlogView = Loadable(lazy(() => import('pages/Blogs/blogView')));
const BlogCreate = Loadable(lazy(() => import('pages/Blogs/createBlogs')));

// Settings
const AccountSettings = Loadable(lazy(() => import('pages/Settings/AccountSettings')));

// Categories
const CategoryList = Loadable(lazy(() => import('pages/category/index')));
const AddCategory = Loadable(lazy(() => import('pages/category/AddCategory')));

// Offers
const AddOffer = Loadable(lazy(() => import('pages/offer/AddOffer')));
const OfferCard = Loadable(lazy(() => import('pages/offer/OfferCard')));
const OfferTable = Loadable(lazy(() => import('pages/offer/OfferTable')));

// Contests
const Contest = Loadable(lazy(() => import('pages/contest/contests')));
const AllContest = Loadable(lazy(() => import('pages/contest/AllContest')));
const ViewContest = Loadable(lazy(() => import('pages/contest/viewContest')));
const LeaderBoard = Loadable(lazy(() => import('pages/contest/LeaderBoard')));
const ContestForm = Loadable(lazy(() => import('pages/contest/contestForm')));
const ContestData = Loadable(lazy(() => import('pages/contest/contestData')));
const ContestTable = Loadable(lazy(() => import('pages/contest/contestTable')));
const QuizLeaderboard = Loadable(lazy(() => import('pages/contest/QuizLeaderboard')));
const ContestleaderBoard = Loadable(lazy(() => import('pages/contest/ContestLeaderboard')));

// Import/Export
const Export = Loadable(lazy(() => import('pages/importExport/export')));
const Import = Loadable(lazy(() => import('pages/importExport/import')));

// Meetings
const MeetingForm = Loadable(lazy(() => import('pages/meeting/meetingForm')));
const MeetingList = Loadable(lazy(() => import('pages/meeting/meetingList')));
const MeetingReports = Loadable(lazy(() => import('pages/meeting/meetingReport')));
const MeetingSummary = Loadable(lazy(() => import('pages/meeting/meetingSummary')));

// Events
const Event = Loadable(lazy(() => import('pages/event/calendar')));
const EventTable = Loadable(lazy(() => import('pages/event/EventTable')));
const RequestTabs = Loadable(lazy(() => import('pages/event/RequestTabs')));
const RequestTable = Loadable(lazy(() => import('pages/event/RequestTable')));

// Pages routing
const AuthLogin = Loadable(lazy(() => import('pages/auth/login')));
const AuthRegister = Loadable(lazy(() => import('pages/auth/register')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/check-mail')));
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/reset-password')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/forgot-password')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/code-verification')));
const MaintenanceAwayScreen = Loadable(lazy(() => import('pages/maintenance/away-screen')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon')));
const TwoFactorAuthentication = Loadable(lazy(() => import('pages/auth/twoFactorAuthentication')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));

// Sample pages
const PricingPage = Loadable(lazy(() => import('pages/extra-pages/pricing')));
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));

// Timetables
const timeTable = Loadable(lazy(() => import('pages/timeTable/timeTableView')));
const StudentList = Loadable(lazy(() => import('pages/time-table/studentList')));
const AddTimeTable = Loadable(lazy(() => import('pages/time-table/AddTimeTable')));
const TimeTableList = Loadable(lazy(() => import('pages/time-table/TimeTable-Table')));

// Classrooms
const ClassRoomList = Loadable(lazy(() => import('pages/classRoom/classRoomList')));
const ClassRoomForm = Loadable(lazy(() => import('pages/classRoom/classRoomForm')));

// Chat
const AppChat = Loadable(lazy(() => import('pages/chat/Chat')));

// Transfer students
const TransferStudents = Loadable(lazy(() => import('pages/transferStudents/UserTable')));

// Skill Metrics
const Skill = Loadable(lazy(() => import('pages/skill/Skill')));

// Exam Timetable
const TimeTableScheduler = Loadable(lazy(() => import('pages/timetable-scheduler/index')));
const TimeTableForm = Loadable(lazy(() => import('pages/timetable-scheduler/TimeTableForm')));

//Streams
const StreamList = Loadable(lazy(() => import('pages/stream/index')));
const AddStream = Loadable(lazy(() => import('pages/stream/addStream')));

//Divisions
const DivisionList = Loadable(lazy(() => import('pages/division/divisionList')));
const AddDivision = Loadable(lazy(() => import('pages/division/addDivision')));

//Workload
const WorkloadList = Loadable(lazy(() => import('pages/Workload/Workload')));
const AddWorkload = Loadable(lazy(() => import('pages/Workload/addWorkload')));

// ==============================|| MAIN ROUTES ||============================== //
const Route = () => {
  const { user, organization }: any = useAuth();
  const permission = user?.userTypeId?.permission;
  const isSuperAdmin = user?.type == 'superadmin';
  const isTrainingOrg = organization?.trainingMode;
  const canNcvetQuestionBank = canManageNcvetQuestionBankContent(organization, user);
  const ticketRouteBypass =
    user?.type === 'awardingBody' || user?.type === 'awarding-body' || user?.type === 'assessor' || user?.type === 'assessorAdmin';

  const MainRoutes = {
    path: '/',
    children: [
      { path: '', element: <Home /> },
      { path: 'privacy-policy', element: <PrivacyPolicy /> },
      { path: 'terms-of-service', element: <TermsOfServices /> },
      { path: 'account/accomplishments/verify/:id', element: <VerifyCertificate /> },
      { path: 'account/accomplishments/certificate/:id', element: <ViewCertificate /> },
      { path: 'course/info', element: <CourseCatalogWithLayout /> },

      {
        path: '/',
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: 'dashboard',
            children: [
              {
                path: '',
                element: (
                  <PrivateRoute
                    component={DashboardDefault}
                    permission={
                      user?.type === 'superadmin' || user?.type === 'awardingBody' || user?.type === 'awarding-body'
                        ? true
                        : permission?.Dashboard?.View
                    }
                  />
                )
              }
            ]
          },
          {
            path: 'ticket',
            children: [
              {
                path: '',
                element: <PrivateRoute component={AllTicket} permission={ticketRouteBypass || permission?.Tickets?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AddTicket} permission={ticketRouteBypass || permission?.Tickets?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddTicket} permission={ticketRouteBypass || permission?.Tickets?.Update} />
              }
            ]
          },
          {
            path: 'transferstudents',
            children: [
              {
                path: '',
                element: <PrivateRoute component={TransferStudents} permission={permission?.TransferStudents?.View} />
              }
            ]
          },
          {
            path: 'mylearning',
            children: [
              {
                path: '',
                element: <PrivateRoute component={MyCourse} permission={permission?.UserCourses?.View && isTrainingOrg} />
              },
              {
                path: 'course',
                element: <PrivateRoute component={VideoPlayer} permission={permission?.UserCourses?.View && isTrainingOrg} />
              }
            ]
          },
          {
            path: 'subject',
            children: [
              {
                path: '',
                element: <PrivateRoute component={SubjectList} permission={user?.type === 'superadmin'} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={SubjectFormOption} permission={user?.type === 'superadmin'} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={SubjectFormOption} permission={user?.type === 'superadmin'} />
              }
            ]
          },
          {
            path: 'exam',
            children: [
              {
                path: '',
                element: <PrivateRoute component={ExamTable} permission={permission?.Exam?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AddExam} permission={permission?.Exam?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddExam} permission={permission?.Exam?.Update} />
              },
              {
                path: 'section',
                element: <PrivateRoute component={AddExamSection} permission={permission?.Exam?.Create} />
              },
              {
                path: 'assess',
                element: <PrivateRoute component={ResultDetailsTable} permission={permission?.Exam?.View} />
              },
              {
                path: 'checking',
                element: <PrivateRoute component={AnswerChecking} permission={permission?.Exam?.View} />
              },
              {
                path: 'OMRConfig',
                element: <PrivateRoute component={OMRConfig} permission={permission?.OMRConfig?.Update} />
              },
              {
                path: 'OMRConfig',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={OMRConfig} permission={permission?.OMRConfig?.Update} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={AddEditOMRConfig} permission={permission?.OMRConfig?.Update} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={AddEditOMRConfig} permission={permission?.OMRConfig?.Update} />
                  }
                ]
              }
            ]
          },
          {
            path: 'time-table',
            children: [
              {
                path: '',
                element: <PrivateRoute component={TimeTableList} permission={permission?.TimeTable?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AddTimeTable} permission={permission?.TimeTable?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddTimeTable} permission={permission?.TimeTable?.Update} />
              },
              {
                path: 'students',
                element: <PrivateRoute component={StudentList} permission={permission?.TimeTable?.Update} />
              }
            ]
          },
          // {
          //   path: '/timetable-scheduler',
          //   children: [
          //     {
          //       path: '',
          //       element: <PrivateRoute component={TimeTableScheduler} permission={permission?.TimetableScheduler?.View} />
          //     },
          //     {
          //       path: '/timetable-scheduler/add',
          //       element: <PrivateRoute component={TimeTableForm} permission={permission?.TimetableScheduler?.Create} />
          //     },
          //     {
          //       path: '/timetable-scheduler/edit',
          //       element: <PrivateRoute component={TimeTableForm} permission={permission?.TimetableScheduler?.Update} />
          //     }
          //   ]
          // },
          // New Timetable Scheduler Route
          {
            path: '/timetableNew',
            element: <PrivateRoute component={TimeTableViewer} permission={permission?.TimetableNew?.View} />
          },
          {
            path: '/timeOff',
            element: <PrivateRoute component={TimeOff} permission={permission?.TimetableNew?.View} />
          },

          {
            path: 'subjectworkload',
            children: [
              {
                path: '/subjectworkload/',
                element: <PrivateRoute component={WorkloadList} permission={permission?.Workload?.View} />
              },
              {
                path: '/subjectworkload/add',
                element: <PrivateRoute component={AddWorkload} permission={permission?.Workload?.Create} />
              },
              {
                path: '/subjectworkload/edit',
                element: <PrivateRoute component={AddWorkload} permission={permission?.Workload?.Update} />
              }
            ]
          },
          {
            path: 'teacherworkload',
            children: [
              {
                path: '/teacherworkload/',
                element: <PrivateRoute component={TeacherWorkloadList} permission={permission?.Workload?.View} />
              },
              {
                path: '/teacherworkload/add',
                element: <PrivateRoute component={AddTeacherWorkload} permission={permission?.Workload?.Create} />
              },
              {
                path: '/teacherworkload/edit',
                element: <PrivateRoute component={AddTeacherWorkload} permission={permission?.Workload?.Update} />
              }
            ]
          },
          {
            path: '/profile',
            element: <PrivateRoute component={AddUser} permission={true} />
          },
          {
            path: 'superuser',
            children: [
              {
                path: '',
                element: <PrivateRoute component={SuperUsers} permission={true} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={SuperUsersAdd} permission={true} />
              }
            ]
          },
          {
            path: 'discussion',
            children: [
              {
                path: '',
                element: <PrivateRoute component={DiscussionHome} permission={permission?.Discussions?.View} />
              },
              {
                path: 'view',
                element: <PrivateRoute component={DiscussionPage} permission={permission?.Discussions?.View} />
              }
            ]
          },
          {
            path: 'feedback',
            children: [
              {
                path: '',
                element: <PrivateRoute component={FeedbackForm} permission={permission?.Feedback?.View} />
              },
              {
                path: '/feedback/feedbackTable',
                element: <PrivateRoute component={FeedbackTable} permission={permission?.Feedback?.View} />
              }
            ]
          },
          {
            path: 'contact',
            children: [
              {
                path: '/contact/contactTable',
                element: <PrivateRoute component={ContactTable} permission={permission?.Contact?.View} />
              }
            ]
          },
          {
            path: 'Settings',
            children: [
              {
                path: 'accountsettings',
                element: <PrivateRoute component={AccountSettings} permission={permission?.AccountSettings?.View} />
              },
              {
                path: 'contentmanagement',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={ContentManagementList} permission={permission?.AccountSettings?.View} />
                  }
                  // {
                  //   path: 'view',
                  //   element: <PrivateRoute component={ContentManagement} permission={permission?.AccountSettings?.View} />
                  // }
                ]
              }
            ]
          },
          {
            path: 'aiLesson',
            children: [
              {
                path: '',
                element: <PrivateRoute component={AiLessonList} permission={permission?.AiLesson?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AiLessonsForm} permission={permission?.AiLesson?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AiLessonsForm} permission={permission?.AiLesson?.Update} />
              }
            ]
          },
          {
            path: 'notify',
            element: <PrivateRoute component={AllNotify} permission={ticketRouteBypass || permission?.Dashboard?.View} />
          },
          {
            path: 'blog',
            children: [
              {
                path: '/blog',
                element: <PrivateRoute component={Blogs} permission={permission?.Blogs?.View} />
              },
              {
                path: '/blog/view',
                element: <PrivateRoute component={BlogView} permission={permission?.Blogs?.View} />
              },
              {
                path: '/blog/list',
                element: <PrivateRoute component={BlogCreate} permission={permission?.Blogs?.View} />
              },
              {
                path: '/blog/add',
                element: <PrivateRoute component={BlogForm} permission={permission?.Blogs?.Create} />
              },
              {
                path: '/blog/edit',
                element: <PrivateRoute component={BlogForm} permission={permission?.Blogs?.Update} />
              }
            ]
          },
          {
            path: 'contest',
            children: [
              {
                path: '/contest/add',
                element: <PrivateRoute component={ContestForm} permission={permission?.Quiz?.Create} />
              },
              {
                path: '/contest/edit',
                element: <PrivateRoute component={ContestData} permission={permission?.Quiz?.Update} />
              },
              {
                path: '/contest/all',
                element: <PrivateRoute component={AllContest} permission={permission?.Quiz?.View} />
              },
              {
                path: '/contest/leaderboard',
                element: <PrivateRoute component={ContestleaderBoard} permission={permission?.Quiz?.View} />
              },
              {
                path: '/contest',
                element: <PrivateRoute component={ContestTable} permission={permission?.Quiz?.View} />
              },
              {
                path: '/contest/leaderboard',
                element: <PrivateRoute component={ContestleaderBoard} permission={permission?.Quiz?.View} />
              }
            ]
          },
          {
            path: 'users',
            children: [
              {
                path: '/users/admin',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/awardingBody',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View && organization?.ncvetCompliance} />
              },
              {
                path: '/users/faculty',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/learner',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/assessor',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/assessor-agency',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },

              {
                path: '/users/applied_Learner',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/admin',
                element: <PrivateRoute component={UserList} permission={permission?.Users?.View} />
              },
              {
                path: '/users/add',
                element: <PrivateRoute component={AddUser} permission={permission?.Users?.Create} />
              },
              {
                path: '/users/edit',
                element: <PrivateRoute component={AddUser} permission={permission?.Users?.Update} />
              }
            ]
          },
          {
            path: 'attendance',
            children: [
              {
                path: 'attendanceReport',
                element: <PrivateRoute component={AttendanceReport} permission={permission?.Attendance?.View} />
              },
              {
                path: '',
                element: <PrivateRoute component={AttendanceCheck} permission={permission?.Attendance?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AttendanceCheck} permission={permission?.Attendance?.Update} />
              }
            ]
          },
          {
            path: 'category',
            children: [
              {
                path: '/category/',
                element: <PrivateRoute component={CategoryList} permission={permission?.Categories?.View} />
              },
              {
                path: '/category/add',
                element: <PrivateRoute component={AddCategory} permission={permission?.Categories?.Create} />
              },
              {
                path: '/category/edit',
                element: <PrivateRoute component={AddCategory} permission={permission?.Categories?.Update} />
              }
            ]
          },
          {
            path: 'trainingcenter',
            children: [
              {
                path: '',
                element: <PrivateRoute component={TrainingCenterList} permission={permission?.TrainingCenter?.View} />
              },
              {
                path: '/trainingcenter/add',
                element: <PrivateRoute component={AddTrainingCenter} permission={permission?.TrainingCenter?.Create} />
              },
              {
                path: '/trainingcenter/edit',
                element: <PrivateRoute component={AddTrainingCenter} permission={permission?.TrainingCenter?.Update} />
              }
            ]
          },
          {
            path: 'stream',
            children: [
              {
                path: '/stream/',
                element: <PrivateRoute component={StreamList} permission={permission?.Stream?.View} />
              },
              {
                path: '/stream/add',
                element: <PrivateRoute component={AddStream} permission={permission?.Stream?.Create} />
              },
              {
                path: '/stream/edit',
                element: <PrivateRoute component={AddStream} permission={permission?.Stream?.Update} />
              }
            ]
          },
          {
            path: 'subject-info',
            children: [
              {
                path: 'add',
                element: <PrivateRoute component={SubjectFormOption} permission={true} />
              }
            ]
          },
          {
            path: 'division',
            children: [
              {
                path: '/division/',
                element: <PrivateRoute component={DivisionList} permission={permission?.Division?.View} />
              },
              {
                path: '/division/add',
                element: <PrivateRoute component={AddDivision} permission={permission?.Division?.Create} />
              },
              {
                path: '/division/edit',
                element: <PrivateRoute component={AddDivision} permission={permission?.Division?.Update} />
              }
            ]
          },
          {
            path: 'certificate',
            children: [
              {
                path: '/certificate',
                element: <PrivateRoute component={CertificateTable} permission={permission?.Certificates?.View} />
              },
              {
                path: '/certificate/mycertificate',
                element: <PrivateRoute component={UserCertificateTable} permission={permission?.Certificates?.View} />
              },
              {
                path: '/certificate/add',
                element: <PrivateRoute component={MainCertificate} permission={permission?.Certificates?.Create} />
              },
              {
                path: '/certificate/edit',
                element: <PrivateRoute component={MainCertificate} permission={permission?.Certificates?.Update} />
              }
            ]
          },
          {
            path: 'assignment',
            children: [
              {
                path: '',
                element: <PrivateRoute component={AssignmentList} permission={permission?.Assignment?.View} />
              },
              {
                path: 'studentView',
                element: <PrivateRoute component={CheckAssignment} permission={permission?.Assignment?.View} />
              }
            ]
          },
          {
            path: 'emailTemplates',
            children: [
              {
                path: '/emailTemplates',
                element: <PrivateRoute component={EmailTemplates} permission={permission?.EmailTemplates?.View} />
              },
              {
                path: '/emailTemplates/add',
                element: <PrivateRoute component={EmailTemplateForm} permission={permission?.EmailTemplates?.Create} />
              },
              {
                path: '/emailTemplates/edit',
                element: <PrivateRoute component={EmailTemplateForm} permission={permission?.EmailTemplates?.Update} />
              }
            ]
          },
          {
            path: 'group',
            children: [
              {
                path: '/group',
                element: <PrivateRoute component={GroupList} permission={permission?.Groups?.View} />
              },
              {
                path: '/group/add',
                element: <PrivateRoute component={AddGroup} permission={permission?.Groups?.Create} />
              },
              {
                path: '/group/edit',
                element: <PrivateRoute component={GroupData} permission={permission?.Groups?.Update} />
              }
            ]
          },

          {
            path: 'skillSector',
            children: [
              {
                path: '/skillSector',
                element: <PrivateRoute component={SkillSectorList} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/skillSector/add',
                element: (
                  <PrivateRoute component={AddSkillSector} permission={permission?.NCVETComplaince?.Create && user?.type === 'admin'} />
                )
              },
              {
                path: '/skillSector/edit',
                element: (
                  <PrivateRoute component={AddSkillSector} permission={permission?.NCVETComplaince?.Update && user?.type === 'admin'} />
                )
              }
            ]
          },
          {
            path: 'qualificationpack',
            children: [
              {
                path: '/qualificationpack',
                element: <PrivateRoute component={QualificationPackList} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/qualificationpack/add',
                element: <PrivateRoute component={AddQualificationPack} permission={permission?.NCVETComplaince?.Create} />
              },
              {
                path: '/qualificationpack/edit',
                element: <PrivateRoute component={AddQualificationPack} permission={permission?.NCVETComplaince?.Update} />
              }
            ]
          },
          {
            path: 'performanceCriteria',
            children: [
              {
                path: '/performanceCriteria',
                element: <PrivateRoute component={PerformanceCriteriaList} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/performanceCriteria/add',
                element: <PrivateRoute component={AddPerformanceCriteria} permission={permission?.NCVETComplaince?.Create} />
              },
              {
                path: '/performanceCriteria/edit',
                element: <PrivateRoute component={AddPerformanceCriteria} permission={permission?.NCVETComplaince?.Update} />
              }
            ]
          },
          {
            path: '/question-bank',
            element: (
              <PrivateRoute
                component={NcvetQuestionBankList}
                permission={permission?.NCVETComplaince?.View && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          {
            path: '/question-bank/add',
            element: (
              <PrivateRoute
                component={AddNcvetQuestion}
                permission={permission?.NCVETComplaince?.Create && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          {
            path: '/question-bank/edit',
            element: (
              <PrivateRoute
                component={AddNcvetQuestion}
                permission={permission?.NCVETComplaince?.Update && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          { path: '/ncvet-question-bank', element: <Navigate to="/question-bank" replace /> },
          { path: '/ncvet-question-bank/add', element: <Navigate to="/question-bank/add" replace /> },
          { path: '/ncvet-question-bank/edit', element: <Navigate to="/question-bank/edit" replace /> },
          {
            path: '/rubrics',
            element: (
              <PrivateRoute
                component={RubricList}
                permission={permission?.NCVETComplaince?.View && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          {
            path: '/rubrics/add',
            element: (
              <PrivateRoute
                component={AddRubric}
                permission={permission?.NCVETComplaince?.Create && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          {
            path: '/rubrics/edit',
            element: (
              <PrivateRoute
                component={AddRubric}
                permission={permission?.NCVETComplaince?.Update && isOrganizationNcvetEffective(organization) && canNcvetQuestionBank}
              />
            )
          },
          {
            path: 'nos',
            children: [
              {
                path: '/nos',
                element: <PrivateRoute component={NOSList} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/nos/add',
                element: <PrivateRoute component={AddNOS} permission={permission?.NCVETComplaince?.Create} />
              },
              {
                path: '/nos/edit',
                element: <PrivateRoute component={AddNOS} permission={permission?.NCVETComplaince?.Update} />
              }
            ]
          },
          {
            path: 'batch',
            children: [
              {
                path: '/batch',
                element: <PrivateRoute component={BatchList} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/batch/add',
                element: (
                  <PrivateRoute
                    component={AddBatch}
                    permission={permission?.NCVETComplaince?.Create && user?.type !== 'awardingBody' && user?.type !== 'awarding-body'}
                  />
                )
              },
              {
                path: '/batch/edit',
                element: (
                  <PrivateRoute
                    component={AddBatch}
                    permission={permission?.NCVETComplaince?.Update && user?.type !== 'awardingBody' && user?.type !== 'awarding-body'}
                  />
                )
              },
              {
                path: '/batch/courses',
                element: <PrivateRoute component={BatchCourseDetails} permission={permission?.NCVETComplaince?.View} />
              },
              {
                path: '/batch/assessor',
                element: <PrivateRoute component={AddAssessor} permission={permission?.NCVETComplaince?.Update} />
              },
              {
                path: '/batch/students-results',
                element: <PrivateRoute component={BatchStudentsResults} permission={permission?.NCVETComplaince?.View} />
              }
            ]
          },

          {
            path: 'question-group',
            children: [
              {
                path: '',
                element: <PrivateRoute component={QuizGroupTabs} permission={permission?.QuestionGroup?.View} />
              },
              {
                path: '/question-group/add',
                element: <PrivateRoute component={AddQuizGroup} permission={permission?.QuestionGroup?.Create} />
              }
              // {
              //   path: '/question-group/edit',
              //   element: <PrivateRoute component={AddQuizGroup} permission={permission?.QuestionGroup?.Update} />
              // }
            ]
          },

          {
            path: 'importExport',
            children: [
              {
                path: '/importExport/import',
                element: <Import />
              },
              {
                path: '/importExport/export',
                element: <Export />
              }
            ]
          },
          {
            path: 'notification',
            children: [
              {
                path: '',
                element: <PrivateRoute component={Notification} permission={permission?.EventNotification?.View} />
                // element: <Notification />
              },
              {
                path: '/notification/add',
                element: <PrivateRoute component={AddNotification} permission={permission?.EventNotification?.Create} />
              },
              {
                path: '/notification/edit',
                element: <PrivateRoute component={AddNotification} roles={permission?.EventNotification?.Update} />
              },
              {
                path: '/notification/email-template-form',
                element: <PrivateRoute component={CustomEmailTemplateForm} permission={true} />
              }
            ]
          },
          {
            path: 'customNotification',
            children: [
              {
                path: '',
                element: <PrivateRoute component={CustomNotification} permission={permission?.CustomNotification?.View} />
                // element: <Notification />
              },
              {
                path: '/customNotification/add',
                element: <PrivateRoute component={AddCustomNotification} permission={permission?.CustomNotification?.Create} />
              }
              // {
              //   path: '/notification/edit',
              //   element: <PrivateRoute component={AddNotification} roles={permission?.EventNotification?.Update} />
              // }
            ]
          },
          {
            path: 'event',
            children: [
              {
                path: '',
                element: <PrivateRoute component={Event} permission={permission?.Events?.View} />
              },
              {
                path: '/event/list',
                element: <PrivateRoute component={EventTable} permission={permission?.Events?.View} />
              },
              {
                path: '/event/myEvents',
                element: <PrivateRoute component={RequestTable} permission={permission?.Events?.View} />
              },
              {
                path: '/event/request',
                element: <PrivateRoute component={RequestTabs} permission={permission?.Events?.View} />
              }
            ]
          },
          {
            path: 'meeting',
            children: [
              {
                path: '',
                element: <PrivateRoute component={MeetingList} permission={permission?.Meeting?.View} />
              },
              {
                path: '/meeting/add',
                element: <PrivateRoute component={MeetingForm} permission={permission?.Meeting?.Create} />
              },
              {
                path: '/meeting/edit',
                element: <PrivateRoute component={MeetingForm} permission={permission?.Meeting?.Update} />
              }
            ]
          },
          {
            path: 'course',
            children: [
              {
                path: '/course/add',
                element: <PrivateRoute component={CourseForm} permission={permission?.Courses?.Create} />
              },
              {
                path: '/course/wishlist',
                element: <PrivateRoute component={Wishlist} permission={permission?.Courses?.View} />
              },
              {
                path: '/course/edit',
                element: <PrivateRoute component={CourseData} permission={permission?.Courses?.Update} />
              },
              {
                path: '/course',
                element: <PrivateRoute component={CourseListTable} permission={permission?.Courses?.View} />
              },
              {
                path: '/course/assignment',
                element: <PrivateRoute component={AssignmentView} permission={permission?.Assignment?.View} />
              },
              {
                path: '/course/assignment/add',
                element: <PrivateRoute component={AddAssignment} permission={permission?.Assignment?.View} />
              },
              {
                path: '/course/assignment/edit',
                element: <PrivateRoute component={AddAssignment} permission={permission?.Assignment?.View} />
              },
              {
                path: '/course/content',
                element: <PrivateRoute component={CourseDetails} permission={permission?.Courses?.Update ?? isSuperAdmin} />
              },
              {
                path: '/course/content/add',
                element: <PrivateRoute component={EditContentDetail} permission={permission?.Courses?.Update ?? isSuperAdmin} />
              },
              {
                path: '/course/content/edit',
                element: <PrivateRoute component={EditContentDetail} permission={permission?.Courses?.Update ?? isSuperAdmin} />
              },
              {
                path: '/course/catalog',
                element: <PrivateRoute component={CourseCatalogList} permission={permission?.Courses?.View ?? isSuperAdmin} />
              },
              {
                path: '/course/catalog/info',
                element: <PrivateRoute component={CourseCatalog} permission={permission?.Courses?.View ?? isSuperAdmin} />
              },
              {
                path: '/course/Content/OverView',
                element: <CourseOverView />
              },

              {
                path: '/course/reassign',
                element: (
                  <PrivateRoute
                    component={CourseReAssign}
                    permission={(permission?.CourseReassign?.View || isSuperAdmin) && isTrainingOrg}
                  />
                )
              },
              {
                path: '/course/reassign/add',
                element: (
                  <PrivateRoute
                    component={AddCourseReAssign}
                    permission={(permission?.CourseReassign?.Create || isSuperAdmin) && isTrainingOrg}
                  />
                )
              },
              {
                path: '/course/reassign/edit',
                element: <PrivateRoute component={AddCourseReAssign} permission={permission?.Courses?.Create && isTrainingOrg} />
              },
              {
                path: '/course/payment',
                element: <PrivateRoute component={CoursePayment} permission={permission?.CoursePayment?.View} />
              }
            ]
          },
          {
            path: 'whiteboard',
            children: [
              {
                path: '/whiteboard/recordings',
                element: <PrivateRoute component={WhiteboardList} permission={permission?.Whiteboard?.View ?? isSuperAdmin} />
              },
              {
                path: '/whiteboard/board',
                element: <PrivateRoute component={CourseWhiteboard} permission={permission?.Whiteboard?.Create ?? isSuperAdmin} />
              },
              {
                path: '/whiteboard/recordings/edit',
                element: <PrivateRoute component={EditWhiteboardRecording} permission={permission?.Whiteboard?.Update ?? isSuperAdmin} />
              }
            ]
          },
          {
            path: 'offer',
            children: [
              {
                path: '/offer',
                element: <PrivateRoute component={OfferTable} permission={permission?.Offers?.View} />
              },
              {
                path: '/offer/add',
                element: <PrivateRoute component={AddOffer} permission={permission?.Offers?.Create} />
              },
              {
                path: '/offer/edit',
                element: <PrivateRoute component={AddOffer} permission={permission?.Offers?.Update} />
              },
              {
                path: '/offer/view',
                element: <OfferCard />
              }
            ]
          },
          {
            path: 'reports',
            children: [
              {
                path: '/reports/course',
                element: <PrivateRoute component={CourseReports} permission={permission?.Reports?.Courses} />
              },
              {
                path: '/reports/assessment',
                element: <PrivateRoute component={AssessmentReport} permission={permission?.Reports?.Assessments} />
              },
              {
                path: '/reports/user',
                element: <PrivateRoute component={UserReports} permission={permission?.Reports?.Users} />
              },
              {
                path: '/reports/assessor',
                element: <PrivateRoute component={AssessorReport} permission={permission?.Reports?.Assessors} />
              },
              {
                path: '/reports/assessordashboard',
                element: <PrivateRoute component={AssessorDashboard} permission={permission?.Reports?.Assessors} />
              },
              {
                path: '/reports/meeting',
                element: <PrivateRoute component={MeetingReports} permission={permission?.Reports?.Meeting} />
              },
              {
                path: '/reports/meeting/usersummary',
                element: <PrivateRoute component={MeetingSummary} permission={permission?.Reports?.Meeting} />
              },
              {
                path: '/reports/userdashboard',
                element: <PrivateRoute component={UserDashboard} permission={permission?.Reports?.Users} />
              },
              {
                path: '/reports/coursedashboard',
                element: <PrivateRoute component={CourseDashboard} permission={permission?.Reports?.Courses} />
              },
              {
                path: '/reports/coursedashboard/assignmentUserTable',
                element: <PrivateRoute component={AssignmentUserTable} permission={permission?.Reports?.Courses} />
              },
              {
                path: '/reports/assessmentdashboard',
                element: <PrivateRoute component={AssessmentDashboard} permission={permission?.Reports?.Assessments} />
              },
              {
                path: '/reports/weekly-report',
                element: <PrivateRoute component={WeeklyReportList} permission={permission?.Reports?.Meeting} />
              },
              {
                path: '/reports/badges',
                element: <Badges />
              }
            ]
          },
          {
            path: 'quiz',
            children: [
              // {
              //   path: '',
              //   element: <PrivateRoute component={QuizView} permission={true} />
              // },
              {
                path: 'assessment/add',
                element: <PrivateRoute component={OverallAssessment} permission={permission?.Questions?.Create} />
              },

              {
                path: 'assessment/edit',
                element: <PrivateRoute component={OverallAssessment} permission={permission?.Questions?.Update} />
              },
              {
                path: 'assessment',
                element: <PrivateRoute component={AssessmentList} permission={permission?.Questions?.View} />
              },
              {
                path: 'test',
                element: <PrivateRoute component={QuizStepper} permission={permission?.Quiz?.Attempt} />
              },
              {
                path: 'questions',
                element: <PrivateRoute component={QuestionTable} permission={permission?.Questions?.View} />
              },
              // {
              //   path: 'select',
              //   element: <PrivateRoute component={QuestionList} permission={permission?.Questions?.View} />
              // },
              {
                path: 'questions/add',
                element: <PrivateRoute component={AddQuestions} permission={permission?.Questions?.Create} />
              },
              {
                path: 'questions/edit',
                element: <PrivateRoute component={AddQuestions} permission={permission?.Questions?.Update} />
              },
              // {
              //   path: 'report',
              //   element: <PrivateRoute component={Report} permission={true} />
              // },
              // {
              //   path: 'resulthistory',
              //   element: <PrivateRoute component={ResultHistory} permission={true} />
              // },
              {
                path: 'leaderboard',
                element: <PrivateRoute component={QuizLeaderboard} permission={permission?.Quiz?.View} />
              }
            ]
          },
          {
            path: 'badges',
            children: [
              {
                path: 'add',
                element: <PrivateRoute component={AddBadgeForm} permission={permission?.Badges?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddBadgeForm} permission={permission?.Badges?.Update} />
              },
              {
                path: '',
                element: <PrivateRoute component={BadgesList} permission={permission?.Badges?.View} />
              }
            ]
          },
          {
            path: 'permissions',
            children: [
              {
                path: '/permissions',
                element: <PrivateRoute component={UserTypeList} permission={permission?.UserTypes?.View} />
              },
              {
                path: '/permissions/add',
                element: <PrivateRoute component={UserTypeForm} permission={permission?.UserTypes?.Create} />
              },
              {
                path: '/permissions/edit',
                element: <PrivateRoute component={UserTypeForm} permission={permission?.UserTypes?.Update} />
              }
            ]
          },
          {
            path: 'checkout',
            element: <PrivateRoute component={Checkout} permission={permission?.UserCourses?.Purchase} />
          },
          {
            path: 'order-history',
            element: <PrivateRoute component={OrderHistory} permission={permission?.UserCourses?.Purchase} />
          },
          {
            path: 'sample-page',
            element: <SamplePage />
          },
          {
            path: 'pricing',
            element: <PricingPage />
          },
          {
            path: 'package',
            element: <PrivateRoute component={Packages} permission={permission?.Packages?.View} />
          },
          {
            path: 'exam',
            children: [
              {
                path: '',
                element: <PrivateRoute component={ExamTable} permission={permission?.Exam?.View} />
              },

              {
                path: 'add',
                element: <PrivateRoute component={AddExam} permission={permission?.Exam?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddExam} permission={permission?.Exam?.Update} />
              },
              {
                path: 'section',
                element: <PrivateRoute component={AddExamSection} permission={permission?.Exam?.Create} />
              },
              {
                path: 'assess',
                element: <PrivateRoute component={ResultDetailsTable} permission={permission?.Exam?.View} />
              },
              {
                path: 'checking',
                element: <PrivateRoute component={AnswerChecking} permission={permission?.Exam?.View} />
              }
            ]
          },
          {
            path: 'time-table',
            children: [
              {
                path: '',
                element: <PrivateRoute component={ResultTable} permission={permission?.TimeTable?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AddTimeTable} permission={permission?.TimeTable?.Create} />
              },
              {
                path: 'edit',
                element: <PrivateRoute component={AddTimeTable} permission={permission?.TimeTable?.Update} />
              },
              {
                path: 'students',
                element: <PrivateRoute component={StudentList} permission={permission?.TimeTable?.Update} />
              }
            ]
          },
          {
            path: 'settings',
            children: [
              {
                path: 'accountsettings',
                element: <PrivateRoute component={AccountSettings} permission={permission?.AccountSettings?.View} />
              }
            ]
          },
          {
            path: 'chat',
            element: <PrivateRoute component={AppChat} permission={permission?.Chats?.View} />
          },
          {
            path: 'plan-learning',
            element: <PrivateRoute component={AIChatBot} permission={permission?.AIChat?.View} />
          },
          {
            path: '/mylearning',
            element: <PrivateRoute component={SuperAdminCourseList} permission={permission?.Courses?.View} />
          },
          {
            path: '/view-course',
            element: <PrivateRoute component={SuperAdminCourseView} permission={permission?.Courses?.View} />
          },
          {
            path: '/',
            element: (
              <AuthGuard>
                <ContentLayout hideDrawer={true} isStudent={false} />
              </AuthGuard>
            ),
            children: [
              {
                path: 'mylearning',
                children: [
                  {
                    path: '/mylearning/course',
                    element: <PrivateRoute component={VideoPlayer} permission={permission?.UserCourses?.View} />
                  }
                ]
              }
            ]
          },
          {
            path: '/maintenance',
            element: <CommonLayout />,
            children: [
              {
                path: '404',
                element: <MaintenanceError />
              },
              {
                path: '500',
                element: <MaintenanceError500 />
              },
              {
                path: 'away-screen',
                element: <MaintenanceAwayScreen />
              },
              {
                path: 'under-construction',
                element: <MaintenanceUnderConstruction />
              },
              {
                path: 'coming-soon',
                element: <MaintenanceComingSoon />
              }
            ]
          },
          {
            path: '/auth',
            element: <CommonLayout />,
            children: [
              {
                path: 'login',
                element: <AuthLogin />
              },
              {
                path: 'register',
                element: <AuthRegister />
              },
              {
                path: 'forgot-password',
                element: <AuthForgotPassword />
              },
              {
                path: 'reset-password',
                element: <AuthResetPassword />
              },
              {
                path: 'check-mail',
                element: <AuthCheckMail />
              },
              {
                path: 'code-verification',
                element: <AuthCodeVerification />
              },
              {
                path: 'two-factor-authentication',
                element: <TwoFactorAuthentication />
              }
            ]
          },
          {
            path: 'getStarted',
            children: [
              {
                path: '',
                element: <PrivateRoute component={GetStarted} permission={true} />
              }
            ]
          },
          {
            path: 'timeline',
            children: [
              {
                path: '',
                element: <PrivateRoute component={TimeLineTable} permission={permission?.Timeline?.View} />
              }
            ]
          },

          {
            path: 'classroom',
            children: [
              {
                path: '/classroom',
                element: <PrivateRoute component={ClassRoomList} permission={user?.type === 'superadmin'} />
              },
              {
                path: '/classroom/add',
                element: <PrivateRoute component={ClassRoomForm} permission={user?.type === 'superadmin'} />
              },
              {
                path: '/classroom/edit',
                element: <PrivateRoute component={ClassRoomForm} permission={user?.type === 'superadmin'} />
              }
            ]
          }
        ]
      },
      // learner routes
      {
        path: '/learner',
        element: (
          <AuthGuard>
            <ContentLayout hideDrawer={true} isStudent={true} />
          </AuthGuard>
        ),
        children: [
          {
            path: '',
            children: [
              {
                path: '',
                element: <PrivateRoute component={LearnerDashboard} permission={permission?.Dashboard?.View} />
              },
              // {
              //   path: 'jee',
              //   element: <PrivateRoute component={JEEQuestion} permission={permission?.Exam?.View} />
              // },
              {
                path: 'packages',
                element: <PrivateRoute component={StudentPackages} permission={permission?.Subscription?.View} />
              },
              {
                path: 'skills',
                element: <PrivateRoute component={Skill} permission={permission?.Skills?.View} />
              },
              {
                path: 'dictionary',
                element: <PrivateRoute component={Dictionary} permission={true} />
              },
              // {
              //   path: 'chat',
              //   children: [
              //     {
              //       path: '',
              //       element: <PrivateRoute component={AppChat} permission={permission?.Chats?.View} />
              //     }
              //   ]
              // },
              {
                path: 'assignments',
                children: [
                  { path: '', element: <PrivateRoute component={Assignment} permission={permission?.Courses?.View} /> },
                  { path: 'view', element: <PrivateRoute component={ViewAssignment} permission={permission?.Courses?.View} /> }
                ]
              },
              {
                path: 'timetable',
                element: <PrivateRoute component={timeTable} permission={permission?.TimeTable?.View} />
              },
              {
                path: 'ticket',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={TcketTable} permission={permission?.Tickets?.View} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={AddTicket} permission={permission?.Tickets?.Create} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={AddTicket} permission={permission?.Tickets?.Update} />
                  }
                ]
              },
              {
                path: 'result',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={ResultTable} permission={permission?.Result?.View} />
                  },
                  {
                    path: 'reportcard',
                    element: <PrivateRoute component={ResultReportCard} permission={permission?.Result?.View} />
                  },
                  {
                    path: 'finalresult',
                    element: <PrivateRoute component={FinalReportCard} permission={permission?.Result?.View} />
                  }
                ]
              },
              {
                path: 'demo',
                element: <PrivateRoute component={DemoExam} permission={permission?.Dashboard?.View} />
              },
              {
                path: 'test',
                element: <PrivateRoute component={ExamStepper} permission={permission?.Dashboard?.View} />
              },
              {
                path: 'freeCourses',
                element: <PrivateRoute component={FreeCoursesList} permission={permission?.Dashboard?.View} />
              },
              {
                path: 'notify',
                element: <PrivateRoute component={AllNotify} permission={permission?.Dashboard?.View} />
              },
              {
                path: 'course',
                element: <PrivateRoute component={CourseCatalogList} permission={permission?.Courses?.View} />
              },
              {
                path: 'wishlist',
                element: <PrivateRoute component={Wishlist} permission={permission?.Courses?.View} />
              },
              {
                path: 'order-history',
                element: <PrivateRoute component={OrderHistory} permission={permission?.UserCourses?.Purchase} />
              },
              {
                path: 'my-activity',
                element: <PrivateRoute component={MyActivity} permission={true} />
              },
              {
                path: 'contest',
                element: <PrivateRoute component={Contest} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'contest/all',
                element: <PrivateRoute component={AllContest} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'globalleaderboard',
                element: <PrivateRoute component={LeaderBoard} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'contest/leaderboard',
                element: <PrivateRoute component={ContestleaderBoard} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'contest/view',
                element: <PrivateRoute component={ViewContest} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'course/info',
                element: <PrivateRoute component={CourseCatalog} permission={permission?.Courses?.View} />
              },
              {
                path: 'certificate/mycertificate',
                element: <PrivateRoute component={UserCertificateTable} permission={permission?.Certificates?.View} />
              },
              {
                path: 'events',
                element: <PrivateRoute component={EventTable} permission={true} />
              },
              {
                path: 'leaderboard',
                element: <PrivateRoute component={QuizLeaderboard} permission={permission?.Quiz?.LeaderBoard} />
              },
              {
                path: 'badges',
                element: <PrivateRoute component={BadgesAchieved} permission={permission?.Badges?.View} />
              },
              {
                path: 'timeline',
                element: <PrivateRoute component={TimeLineTable} permission={permission?.Timeline?.View} />
              },
              // {
              //   path: 'groups',
              //   children: [
              //     {
              //       path: '',
              //       element: <PrivateRoute component={GroupsList} permission={permission?.Groups?.View} />
              //     },
              //     {
              //       path: 'info',
              //       element: <PrivateRoute component={GroupCatalog} permission={permission?.Groups?.View} />
              //     }
              //   ]
              // },
              {
                path: 'discussion',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={DiscussionHome} permission={permission?.Discussions?.View} />
                  },
                  {
                    path: 'view',
                    element: <PrivateRoute component={DiscussionPage} permission={permission?.Discussions?.View} />
                  }
                ]
              },
              {
                path: 'settings',
                children: [
                  {
                    path: 'accountSettings',
                    element: <PrivateRoute component={AccountSettings} permission={permission?.AccountSettings?.View} />
                  }
                ]
              }
            ]
          },
          {
            path: 'mylearning',
            children: [
              {
                path: '',
                element: <PrivateRoute component={MyCourse} permission={permission?.UserCourses?.View} />
              },
              {
                path: 'course',
                element: <PrivateRoute component={VideoPlayer} permission={permission?.UserCourses?.View} />
              }
            ]
          },
          {
            path: 'recordings',
            children: [
              {
                path: '',
                element: <PrivateRoute component={RecordingsList} permission={permission?.Whiteboard?.View} />
              },
              {
                path: 'view',
                element: <PrivateRoute component={RecordingPlayer} permission={permission?.Whiteboard?.View} />
              }
            ]
          },
          {
            path: 'checkout',
            element: <PrivateRoute component={Checkout} permission={permission?.UserCourses?.Purchase} />
          },
          {
            path: 'quiz',
            children: [
              {
                path: '',
                element: <PrivateRoute component={QuizView} permission={permission?.Quiz?.View} />
              },
              {
                path: 'test',
                element: <PrivateRoute component={QuizStepper} permission={permission?.Quiz?.Attempt} />
              },
              {
                path: 'skilltest',
                element: <PrivateRoute component={SkillMatrixStepper} permission={permission?.Quiz?.Attempt} />
              },
              {
                path: 'contest',
                element: <PrivateRoute component={QuizView} permission={permission?.Quiz?.Contest} />
              },
              {
                path: 'resulthistory',
                element: <PrivateRoute component={ResultHistory} permission={permission?.Quiz?.View} />
              }
            ]
          },
          { path: 'account/accomplishments/verify/:id', element: <PrivateRoute component={VerifyCertificate} permission={true} /> },
          { path: 'account/accomplishments/certificate/:id', element: <PrivateRoute component={ViewCertificate} permission={true} /> },
          {
            path: 'event',
            children: [
              {
                path: '',
                element: <PrivateRoute component={Event} permission={true} />
              }
            ]
          },
          {
            path: 'profile',
            element: <PrivateRoute component={AddUser} permission={permission?.Users?.Update} />
          },
          {
            path: 'blog',
            children: [
              {
                path: '',
                element: <PrivateRoute component={Blogs} permission={permission?.Blogs?.View} />
              },
              {
                path: 'view',
                element: <PrivateRoute component={BlogView} permission={permission?.Blogs?.View} />
              }
            ]
          },
          {
            path: 'feedback',
            children: [
              {
                path: '',
                element: <PrivateRoute component={FeedbackForm} permission={permission?.Feedback?.View} />
              }
              // {
              //   path: '/feedback/feedbackTable',
              //   element: <PrivateRoute component={FeedbackTable} permission={permission?.Feedback?.View} />
              // }
            ]
          },
          {
            path: 'aiLesson',
            children: [
              {
                path: '',
                element: <PrivateRoute component={AiLessonList} permission={permission?.AiLesson?.View} />
              },
              {
                path: 'add',
                element: <PrivateRoute component={AiLessonsForm} permission={permission?.AiLesson?.Create} />
              }
            ]
          },
          {
            path: 'Settings',
            children: [
              {
                path: 'accountsettings',
                element: <PrivateRoute component={AccountSettings} roles={permission?.AccountSettings?.View} />
              }
            ]
          }
          // {
          //   path: 'feedback',
          //   children: [
          //     {
          //       path: '',
          //       element: <PrivateRoute component={FeedbackForm} permission={permission?.Feedback?.View} />
          //     }
          //     // {
          //     //   path: '/feedback/feedbackTable',
          //     //   element: <PrivateRoute component={FeedbackTable} permission={permission?.Feedback?.View} />
          //     // }
          //   ]
          // }
          // {
          //   path: 'Settings',
          //   children: [
          //     {
          //       path: 'accountsettings',
          //       element: <PrivateRoute component={AccountSettings} roles={true} />
          //     }
          //   ]
          // }
        ]
      },
      // teacher routes
      {
        path: '/faculty',
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '',
            children: [
              {
                path: '',
                element: <PrivateRoute component={FacultyDashboard} permission={permission?.Dashboard?.View} />
              },
              {
                path: 'course/info',
                element: <PrivateRoute component={CourseCatalog} permission={permission?.Courses?.View} />
              },
              {
                path: 'users/edit',
                element: <PrivateRoute component={AddUser} permission={permission?.Users?.Update} />
              },
              {
                path: 'checkout',
                element: <PrivateRoute component={Checkout} permission={permission?.UserCourses?.Purchase} />
              }
              // {
              //   path: 'course',
              //   element: <PrivateRoute component={CourseCatalogList} roles={all} />
              // },
              // {
              //   path: 'course/info',
              //   element: <PrivateRoute component={CourseCatalog} roles={all} />
              // }
            ]
          }
          // {
          //   path: 'mylearning',
          //   children: [
          //     {
          //       path: '',
          //       element: <PrivateRoute component={MyCourse} roles={all} />
          //     },
          //     {
          //       path: 'course',
          //       element: <PrivateRoute component={VideoPlayer} roles={all} />
          //     }
          //   ]
          // },
          // {
          //   path: 'checkout',
          //   element: <PrivateRoute component={Checkout} roles={all} />
          // },
          // {
          //   path: 'Settings',
          //   children: [
          //     {
          //       path: 'accountsettings',
          //       element: <PrivateRoute component={AccountSettings} roles={all} />
          //     },
          //     {
          //       path: 'certificate/add',
          //       element: <PrivateRoute component={Certificate} roles={all} />
          //     },
          //     {
          //       path: 'certificate/edit',
          //       element: <PrivateRoute component={Certificate} roles={all} />
          //     }
          //   ]
          // }
        ]
      },
      // superadminroutes
      {
        path: '/superadmin',
        element: (
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: '',
            children: [
              {
                path: 'dashboard',
                element: <PrivateRoute component={DashboardDefault} permission={true} />
              },
              {
                path: 'organization',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={Organization} permission={true} />
                  },
                  {
                    path: 'info',
                    element: <PrivateRoute component={OrgTabs} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={AddOrganization} permission={true} />
                  },
                  {
                    path: 'users/info/',
                    element: <PrivateRoute component={UserDashboard} permission={true} />
                  },
                  {
                    path: 'courses/info/',
                    element: <PrivateRoute component={CourseDashboard} permission={true} />
                  },
                  {
                    path: 'assessments/info/',
                    element: <PrivateRoute component={AssessmentDashboard} permission={true} />
                  }
                ]
              },
              {
                path: 'user',
                children: [
                  {
                    path: 'list',
                    element: <PrivateRoute component={SuperUsers} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={SuperUsersAdd} permission={true} />
                  }
                ]
              },
              {
                path: 'course',
                children: [
                  {
                    path: 'list',
                    element: <PrivateRoute component={CourseTableSA} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={CourseFormSA} permission={true} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={CourseFormSA} permission={true} />
                  }
                ]
              },
              {
                path: 'contentmanagement',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={ContentManagementList} permission={true} />
                  }
                  // {
                  //   path: 'view',
                  //   element: <PrivateRoute component={ContentManagement} permission={true} />
                  // }
                ]
              },
              {
                path: 'landingpagemanagement',
                children: [
                  // {
                  //   path: '',
                  //   element: <PrivateRoute component={LandingPageList} permission={true} />
                  // },
                  {
                    path: '',
                    element: (
                      <TabProvider>
                        <PrivateRoute component={LandingPageTabs} permission={true} />
                      </TabProvider>
                    )
                  },
                  {
                    path: 'view',
                    element: <PrivateRoute component={FormRenderer} permission={true} />
                  }
                ]
              },
              {
                path: 'question-group',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={QuizGroupList} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={AddQuizGroup} permission={true} />
                  },
                  {
                    path: 'organizations',
                    element: <PrivateRoute component={AssignedOrgnizationList} permission={true} />
                  }
                ]
              },
              {
                path: 'quiz',
                children: [
                  {
                    path: 'questions',
                    element: <PrivateRoute component={QuestionTable} permission={true} />
                  },
                  {
                    path: 'questions/add',
                    element: <PrivateRoute component={AddQuestions} permission={true} />
                  },
                  {
                    path: 'questions/edit',
                    element: <PrivateRoute component={AddQuestions} permission={true} />
                  }
                ]
              },
              {
                path: 'subject-info',
                // children: [
                //   {
                //     path: '',
                //     element: <PrivateRoute component={SubjectFormOption} permission={true} />
                //   },
                // ]
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={SubjectList} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={SubjectFormOption} permission={true} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={SubjectFormOption} permission={true} />
                  }
                ]
              },
              {
                path: 'board-info',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={BoardInfo} permission={true} />
                  }
                ]
              },
              {
                path: 'assign-questions',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={AssignOrganization} permission={true} />
                  }
                ]
              },
              {
                path: 'customNotification',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={CustomNotification} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={AddCustomNotification} permission={true} />
                  }
                  // {
                  //   path: '/notification/edit',
                  //   element: <PrivateRoute component={AddNotification} roles={permission?.EventNotification?.Update} />
                  // }
                ]
              },
              {
                path: 'errorLogs',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={ErrorLogs} permission={true} />
                  }
                ]
              },
              {
                path: 'email-template',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={EmailTemplateList} permission={true} />
                  },
                  {
                    path: 'email-template-form',
                    element: <PrivateRoute component={EmailTemplateForm} permission={true} />
                  }
                  // {
                  //   path: '/notification/edit',
                  //   element: <PrivateRoute component={AddNotification} roles={permission?.EventNotification?.Update} />
                  // }
                ]
              },
              {
                path: 'package',
                children: [
                  {
                    path: 'add',
                    element: <PrivateRoute component={addPackage} permission={true} />
                  },
                  // {
                  //   path: 'view',
                  //   element: <PrivateRoute component={packages} permission={true} />
                  // },
                  {
                    path: 'assign',
                    element: <PrivateRoute component={assignPackage} permission={true} />
                  },
                  {
                    path: 'history',
                    element: <PrivateRoute component={Packagehistory} permission={true} />
                  },
                  {
                    path: 'list',
                    element: <PrivateRoute component={packageList} permission={true} />
                  }
                ]
              },
              {
                path: 'blog',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={Blogs} permission={true} />
                  },
                  {
                    path: 'view',
                    element: <PrivateRoute component={BlogView} permission={true} />
                  },
                  {
                    path: 'list',
                    element: <PrivateRoute component={BlogCreate} permission={true} />
                  },
                  {
                    path: 'add',
                    element: <PrivateRoute component={BlogForm} permission={true} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={BlogForm} permission={true} />
                  }
                ]
              },
              {
                path: 'contacts',
                children: [
                  {
                    path: 'list',
                    element: <PrivateRoute component={ContactTable} permission={true} />
                  }
                ]
              },
              {
                path: 'appVersion',
                children: [
                  {
                    path: 'update',
                    element: <PrivateRoute component={AppVersionList} permission={true} />
                  }
                ]
              },
              {
                path: 'appPackage',
                children: [
                  {
                    path: 'add',
                    element: <PrivateRoute component={addAppPackage} permission={true} />
                  },
                  {
                    path: 'view',
                    element: <PrivateRoute component={appPackage} permission={true} />
                  },
                  {
                    path: 'edit',
                    element: <PrivateRoute component={addAppPackage} permission={true} />
                  }
                ]
              },
              {
                path: 'ticket',
                children: [
                  {
                    path: '',
                    element: <PrivateRoute component={AllTicket} permission={true} />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: '*',
        element: <PrivateRoute component={MaintenanceError} permission={true} />
      }
    ]
  };
  return MainRoutes;
};
export default Route;
